import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  CustomizationsState,
  ViewerCustomizationInterface,
  ViewerGroupInputItemsInterface,
  ViewerGroupInterface,
  ViewerItemInterface,
  ViewerOptionInterface,
  ViewerOptionInterfaceInput,
} from '../../core';

import MenuGroups from '../MenuGroups';
import MenuChoice from '../MenuChoice';
import { Share as ShareIcon } from '../../icons';
import { Pdf as PdfIcon } from '../../icons';

import styles from './Menu.module.scss';
import { isAddToCartDisabled } from '../../core/utils/functions';
import { useCustomizationParams, useViewerState } from '../../context/ViewerContext';
import useSelectionHook from '../../hooks/use-selection';
import { DescriptionField } from '../DescriptionField';

export type MenuProps = {
  groups: ViewerGroupInterface[];
  isCartLoading: boolean;
  bulkData: ViewerGroupInputItemsInterface | null;
  setBulkDataOptionValue: (option: ViewerOptionInterfaceInput, value: string) => void;
  showOptionPreview: (option: ViewerOptionInterface) => void;
  saveText: (customization: ViewerCustomizationInterface, text: string | null) => void;
  saveFont: (customization: ViewerCustomizationInterface, font: string) => void;
  onAddImageItem: (guid: number, areaId: number) => Promise<void>;
  onItemRemove: (guid: string) => Promise<void>;
  showCustomizationInfo: (info: ViewerCustomizationInterface) => void;
  addToCart: () => void;
  share: () => void;
  pdf: () => void;
  price: string | null;
  pageTitle: string;
  pageDescription: string;
  optionSelected: (option: ViewerItemInterface) => void;
  customizationsState: CustomizationsState;
  setCustomizationsState: (customizationsState: CustomizationsState) => void;
  disableCart: boolean | undefined;
  descriptionFieldValue?: string | null;
};

const Menu: React.FC<MenuProps> = (props) => {
  const [t] = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<ViewerGroupInterface | null>(null);
  const { viewerState } = useViewerState();
  const { onSelectGroup } = useSelectionHook();
  const { customizationParams } = useCustomizationParams();
  const [disableAddToCart, setDisableAddToCart] = useState(false);
  useEffect(() => {
    setSelectedGroup(props.groups?.find((grp) => grp.id === viewerState.groupId) || null);
  }, [props.groups, viewerState.groupId]);

  const sortItems = <T extends { displayOrder: number }>(items: T[]): T[] => {
    return (items || []).sort((a, b) => a.displayOrder - b.displayOrder);
  };

  return (
    <div className={styles.ff_menu} data-testid='menu'>
      {props.pageTitle && <h1 className={styles.ff_menu__title}>{props.pageTitle}</h1>}
      {props.pageDescription && (
        <details className={styles.ff_menu__pageDescription}>
          <summary>{t('PRODUCT_DESCRIPTION', 'Click for Product Description')}</summary>
          <div dangerouslySetInnerHTML={{ __html: props.pageDescription }} />
        </details>
      )}
      {customizationParams?.descriptionField && <DescriptionField descriptionFieldValue={props.descriptionFieldValue} />}
      {props.groups.length && <MenuGroups groups={sortItems(props.groups)} selectedGroupId={viewerState.groupId || null} onSelect={onSelectGroup} />}
      {selectedGroup && (
        <MenuChoice
          group={selectedGroup}
          selectedStepId={viewerState.stepId}
          selectedAttributeId={viewerState.attributeId}
          showCustomizationInfo={props.showCustomizationInfo}
          showOptionPreview={props.showOptionPreview}
          optionSelect={props.optionSelected}
          customizationsState={props.customizationsState}
          setCustomizationsState={props.setCustomizationsState}
          onItemRemove={props.onItemRemove}
          onAddImageItem={props.onAddImageItem}
          bulkData={props.bulkData}
          setBulkDataOptionValue={props.setBulkDataOptionValue}
        />
      )}

      <div className={styles.ff_menu__footer}>
        {
          <div className={styles.ff_menu__price}>
            <div className={styles.ff_menu__price__text}>{t('PRICE')}</div>
            <div className={styles.ff_menu__price__value}>{props.price!}</div>
          </div>
        }
        <div className={styles.ff_menu__actions}>
          {!customizationParams?.pdfHidden && (
            <div className={classNames(styles.ff_menu__btn__pdf)} onClick={() => props.pdf()}>
              <div className={styles.ff_menu__btn__pdf_icon}>
                <PdfIcon />
              </div>
            </div>
          )}

          <button
            className={classNames('btn', 'btn-primary', styles.ff_menu__btn, styles.ff_menu__btn__cart, {
              [styles.ff_menu__btn__is_cart_loading]: props.isCartLoading,
            })}
            onClick={() => {
              !props.isCartLoading && props.addToCart();
              setDisableAddToCart(true);
            }}
            disabled={isAddToCartDisabled(props.groups) || disableAddToCart || props.disableCart === true}
          >
            {t('ADD_TO_CART')}
          </button>

          {!customizationParams?.shareHidden && (
            <button className={classNames('btn', 'btn-secondary', styles.ff_menu__btn, styles.ff_menu__btn__share)} onClick={() => props.share()}>
              <div className={styles.ff_menu__btn__share_icon}>
                <ShareIcon />
              </div>
              <span className={styles.ff_menu__btn__share_text}>{t('SHARE')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Menu;
